import React from 'react'
import { Film, Clock, DollarSign, Maximize2, Eye, PieChart } from 'lucide-react'
import KPICard from '../../ui/KPICard'
import { PublishedBroadcast } from '../../../services/broadcastService'
import { rowFigures, formatAreaSeconds, formatMoney, formatSeconds } from '../broadcastValuation'

interface Props {
  /** Whatever the page is currently showing. Filtering narrows these. */
  broadcasts: PublishedBroadcast[]
}

/**
 * The library's six figures, summed over the broadcasts in view.
 *
 * VALUE FIRST, CONTEXT LAST. The row reads as an answer followed by its
 * supporting measures, and closes with the two that describe the SET rather
 * than the exposure — how many broadcasts and how much footage those figures
 * were drawn from. Leading with a count made the first thing anyone saw the
 * least interesting number on the page.
 *
 * SUMMED OVER WHAT IS SHOWN, not over everything the customer owns. When
 * date and season filters arrive the cards follow the filter without any
 * further work, which is the point of computing them here from the same
 * array the table renders rather than asking the server for a separate
 * total that could disagree with the rows under it.
 *
 * THE RATIOS ARE WEIGHTED, NOT AVERAGED. Averaging each broadcast's own
 * percentage would let a two-minute clip count as much as a three-hour
 * game. Both are computed as a single ratio of summed parts, so a long
 * broadcast carries the weight it should.
 *
 * MEDIA VALUE CAN BE PARTIAL. A broadcast with no audience has no rate and
 * contributes nothing, so the total understates whenever any are unpriced.
 * The card says how many are priced rather than quietly reporting a smaller
 * number as if it were complete.
 */
const BroadcastLibraryTotals: React.FC<Props> = ({ broadcasts }) => {
  if (broadcasts.length === 0) return null

  const sum = (f: (b: PublishedBroadcast) => number) =>
    broadcasts.reduce((n, b) => n + (Number(f(b)) || 0), 0)

  const sponsoredArea = sum((b) => b.sponsored_area_seconds ?? 0)
  const countedArea = sum((b) => b.counted_area_seconds ?? 0)
  const durationMs = sum((b) => b.duration_ms ?? 0)

  // rowFigures, not a second formula. The table below totals to these
  // cards, and a value computed twice is a value that eventually disagrees
  // with itself one row above the total.
  const priced = broadcasts.filter((b) => b.rate_per_30s != null)
  const value = priced.length === 0
    ? null
    : priced.reduce((n, b) => n + (rowFigures(b).value ?? 0), 0)

  // Measured frames only. A broadcast analysed before broadcast_frames
  // existed returns null and is excluded from both halves of the ratio
  // rather than counted as zero presence.
  const measured = broadcasts.filter((b) => (b.frames_analysed ?? 0) > 0)
  const framesAnalysed = measured.reduce((n, b) => n + (b.frames_analysed ?? 0), 0)
  const framesWithSponsor = measured.reduce((n, b) => n + (b.frames_with_sponsor ?? 0), 0)
  const presence = framesAnalysed > 0 ? framesWithSponsor / framesAnalysed : null

  const share = countedArea > 0 ? sponsoredArea / countedArea : null
  const pct = (v: number | null) => v === null ? '—' : `${(v * 100).toFixed(0)}%`

  return (
    <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-5">
      <KPICard
        label="Media value"
        value={formatMoney(value)}
        icon={DollarSign}
        colorScheme="emerald"
        tooltip={priced.length === broadcasts.length
          ? 'Sponsored exposure across these broadcasts, each priced at its own 30-second rate.'
          : `Sponsored exposure priced at each broadcast's own 30-second rate. ${priced.length} of ${broadcasts.length} are priced — the rest have no audience recorded and contribute nothing, so this is a floor rather than a total.`}
      />
      <KPICard
        label="Area-seconds"
        value={formatAreaSeconds(sponsoredArea)}
        icon={Maximize2}
        colorScheme="blue"
        tooltip="Sponsored screen area multiplied by time, summed across these broadcasts."
      />
      <KPICard
        label="Sponsor on screen"
        value={pct(presence)}
        icon={Eye}
        colorScheme="indigo"
        tooltip="Share of all analysed frames in which at least one sponsor was visible. Weighted by length, so a three-hour game counts for more than a two-minute clip."
      />
      <KPICard
        label="Share of exposure"
        value={pct(share)}
        icon={PieChart}
        colorScheme="emerald"
        tooltip="Sponsored area-seconds as a share of every counted brand across these broadcasts. The rest is competitors and incidental brands."
      />
      <KPICard
        label="Broadcasts"
        value={broadcasts.length}
        icon={Film}
        colorScheme="slate"
        tooltip="Published broadcasts in view. A game split across several files counts once — its parts are folded into it."
      />
      <KPICard
        label="Footage analysed"
        value={formatSeconds(durationMs / 1000)}
        icon={Clock}
        colorScheme="slate"
        tooltip="Total running time analysed across these broadcasts."
      />
    </div>
  )
}

export default BroadcastLibraryTotals
